import React, { useState, useEffect } from 'react';
import { 
  Search, 
  DollarSign, 
  Users, 
  ChevronDown, 
  ChevronUp,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  QrCode,
  Calendar,
  Filter,
  Phone,
  Send,
  Wifi,
  WifiOff,
  Mail,
  FileText,
  ShoppingCart,
  Receipt
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import apiService from '../services/apiService';
import { useCobrancaPixStorage } from '../hooks/useLocalStorage';
import { pixPopupUtils } from '../utils/popupUtils';



export default function CobrancaPix() {
  // Estados
  const [busca, setBusca] = useState('');
  const [tipoBusca, setTipoBusca] = useState('nome');
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [contasReceber, setContasReceber] = useState([]);
  const [expandido, setExpandido] = useState({});
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(null);
  const [apenasComContas, setApenasComContas] = useState(false);
  
  // Estados para tipo de cobrança
  const [tipoCobranca, setTipoCobranca] = useState('contas'); // 'contas' ou 'venda'
  const [sequenciaVenda, setSequenciaVenda] = useState('');
  const [vendaSelecionada, setVendaSelecionada] = useState(null);
  const [loadingVenda, setLoadingVenda] = useState(false);
  
  // Modal de geração de link
  const [modalAberto, setModalAberto] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [valorLink, setValorLink] = useState('');
  const [valorMinimo, setValorMinimo] = useState(0);
  const [mensagem, setMensagem] = useState('');
  const [motivoAcrescimo, setMotivoAcrescimo] = useState('');
  const [celular, setCelular] = useState('');
  const [celularOriginal, setCelularOriginal] = useState('');
  const [email, setEmail] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [cpfCnpjOriginal, setCpfCnpjOriginal] = useState('');
  const [diasExpiracao, setDiasExpiracao] = useState(1); 
  const [loadingLink, setLoadingLink] = useState(false);
  
  // Estado de conexão WhatsApp
  const [whatsappConectado, setWhatsappConectado] = useState(false);
  const [verificandoWhatsapp, setVerificandoWhatsapp] = useState(true);

  // Dados Pix
  const { salvarCobrancaPix } = useCobrancaPixStorage();



  /**
   * ⭐ VERIFICAR CONEXÃO WHATSAPP AO CARREGAR
   */
  useEffect(() => {
    verificarConexaoWhatsApp();
    
    // Verificar a cada 30 segundos
    const interval = setInterval(verificarConexaoWhatsApp, 30000);
    
    return () => clearInterval(interval);
  }, []);

  /**
   * ⭐ FUNÇÃO PARA VERIFICAR CONEXÃO WHATSAPP
   */
  const verificarConexaoWhatsApp = async () => {
    try {
      console.log('🔍 Verificando conexão WhatsApp...');
      
      const response = await apiService.whatsapp.getStatus();
      
      console.log('📥 Status WhatsApp completo:', response);
      
      const conectado = response?.data?.success && response?.data?.data?.connected === true;
      
      setWhatsappConectado(conectado);
      setVerificandoWhatsapp(false);
      
      console.log(conectado ? '✅ WhatsApp conectado' : '❌ WhatsApp desconectado');
    } catch (error) {
      console.error('❌ Erro ao verificar WhatsApp:', error);
      setWhatsappConectado(false);
      setVerificandoWhatsapp(false);
    }
  };

  /**
   * ✅ Formatar telefone brasileiro
   */
  const formatarTelefone = (telefone) => {
    if (!telefone) return '';
    const numero = telefone.replace(/\D/g, '');
    if (numero.length === 11) {
      return numero.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (numero.length === 10) {
      return numero.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numero;
  };

  /**
   * ✅ Limpar telefone para formato internacional
   */
  const limparTelefone = (telefone) => {
    if (!telefone) return '';
    let numero = telefone.replace(/\D/g, '');
    if (numero.length === 11 && numero[0] === '0') {
      numero = numero.substring(1);
    }
    if (numero.length === 10 && ['6', '7', '8', '9'].includes(numero[2])) {
      numero = numero.substring(0, 2) + '9' + numero.substring(2);
    }
    if (numero.length === 11 && !numero.startsWith('55')) {
      numero = '55' + numero;
    }
    return numero;
  };

  /**
   * ⭐ Formatar CPF/CNPJ
   */
  const formatarCpfCnpj = (valor) => {
    if (!valor) return '';
    const numeros = valor.replace(/\D/g, '');
    
    if (numeros.length <= 11) {
      // CPF: 000.000.000-00
      return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      // CNPJ: 00.000.000/0000-00
      return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  /**
   * ⭐ Validar CPF
   */
  const validarCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, '');
    
    if (cpf.length !== 11) return false;
    if (/^(\d)\1+$/.test(cpf)) return false;
    
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;
    
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return false;
    
    return true;
  };

  /**
   * ⭐ Validar CNPJ
   */
  const validarCNPJ = (cnpj) => {
    cnpj = cnpj.replace(/\D/g, '');
    
    if (cnpj.length !== 14) return false;
    if (/^(\d)\1+$/.test(cnpj)) return false;
    
    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    const digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    
    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    
    return true;
  };

  /**
   * ⭐ Validar CPF ou CNPJ
   */
  const validarCpfCnpj = (valor) => {
    if (!valor) return false;
    const numeros = valor.replace(/\D/g, '');
    
    if (numeros.length === 11) {
      return validarCPF(numeros);
    } else if (numeros.length === 14) {
      return validarCNPJ(numeros);
    }
    
    return false;
  };

  /**
   * ✅ Validar email
   */
  const validarEmail = (email) => {
    if (!email) return true;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  /**
   * ⭐ NOVA FUNÇÃO: Buscar venda por sequência
   */
  const buscarVenda = async () => {
    if (!sequenciaVenda.trim() || isNaN(sequenciaVenda)) {
      setErro('Digite um número de sequência válido');
      return;
    }

    setLoadingVenda(true);
    setErro(null);
    setVendaSelecionada(null);

    try {
      console.log('🔍 Buscando venda com sequência:', sequenciaVenda);
      
      const response = await apiService.vendas.buscarPorSequencia(parseInt(sequenciaVenda));
      
      console.log('📥 Resposta completa da API:', response);
      console.log('📥 response.data:', response.data);
      console.log('📥 response.success:', response.success);
      
      // O apiService já retorna response.data, então verificamos diretamente
      const dados = response.data || response;
      
      console.log('📥 Dados processados:', dados);
      
      if (dados && dados.success && dados.venda) {
        const venda = dados.venda;
        
        console.log('✅ Venda encontrada:', venda);
        
        // Calcular valor líquido se não estiver presente
        if (!venda.Valor_Liquido) {
          venda.Valor_Liquido = parseFloat(venda.Preco_Final_Somado || 0) - parseFloat(venda.Desconto_Valor_Somado || 0);
        }
        
        setVendaSelecionada(venda);
        setSucesso(`Venda #${venda.Sequencia} encontrada - Cliente: ${venda.Nome_Cli_For}`);
        
        // Auto-abrir modal após 1 segundo
        setTimeout(() => {
          abrirModalVenda(venda);
        }, 1000);
      } else {
        console.error('❌ Venda não encontrada ou resposta inválida');
        setErro(`Venda com sequência ${sequenciaVenda} não encontrada`);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar venda:', error);
      setErro('Erro ao buscar venda: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingVenda(false);
    }
  };

  /**
   * ⭐ NOVA FUNÇÃO: Abrir modal para venda à vista
   */
  const abrirModalVenda = (venda) => {
    setContaSelecionada(null); // Limpar conta selecionada
    setVendaSelecionada(venda);
    
    const valorVenda = venda.Valor_Liquido || 0;
    setValorLink(valorVenda.toFixed(2));
    setValorMinimo(valorVenda);
    setMensagem(`Pagamento referente à venda #${venda.Sequencia} realizada em ${format(new Date(venda.Data), 'dd/MM/yyyy')}`);
    setMotivoAcrescimo('');
    
    // Telefone - buscar do cliente
    const telefoneCliente = venda.Fone_1_Cli_For || venda.Fone_2_Cli_For || '';
    const telefoneFormatado = formatarTelefone(telefoneCliente);
    setCelular(telefoneFormatado);
    setCelularOriginal(telefoneCliente);
    
    // Email
    setEmail('');
    
    // CPF/CNPJ
    const cpfCnpjCliente = venda.CPF_Cli_For || venda.CNPJ_Cli_For || '';
    const cpfCnpjFormatado = formatarCpfCnpj(cpfCnpjCliente);
    setCpfCnpj(cpfCnpjFormatado);
    setCpfCnpjOriginal(cpfCnpjCliente);
    
    setDiasExpiracao(1);
    setModalAberto(true);
  };

  /**
   * ✅ Buscar clientes
   */
  const buscarClientes = async () => {
    if (!busca.trim()) {
      setErro('Digite um nome ou código para buscar');
      return;
    }

    setLoading(true);
    setErro(null);
    setClientes([]);
    setClienteSelecionado(null);

    try {
      console.log('🔍 Iniciando busca de clientes:', busca);
      
      const response = await apiService.clientes.buscar(busca, 10, 0);
      
      let clientesEncontrados = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          clientesEncontrados = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          clientesEncontrados = response.data.data;
        }
      }

      if (clientesEncontrados.length === 0) {
        setErro('Nenhum cliente encontrado');
        setLoading(false);
        return;
      }

      if (apenasComContas) {
        const clientesComContas = [];
        for (const cliente of clientesEncontrados.slice(0, 10)) {
          try {
            const responseContas = await apiService.clientes.buscarContas(cliente.Ordem);
            let contasEncontradas = [];
            if (responseContas.data) {
              if (responseContas.data.data && Array.isArray(responseContas.data.data)) {
                contasEncontradas = responseContas.data.data;
              } else if (responseContas.data.contas && Array.isArray(responseContas.data.contas)) {
                contasEncontradas = responseContas.data.contas;
              } else if (Array.isArray(responseContas.data)) {
                contasEncontradas = responseContas.data;
              }
            }
            if (contasEncontradas.length > 0) {
              clientesComContas.push(cliente);
            }
          } catch (error) {
            console.error(`❌ Erro ao buscar contas do cliente ${cliente.Codigo}:`, error);
          }
        }
        clientesEncontrados = clientesComContas;
        if (clientesEncontrados.length === 0) {
          setErro('Nenhum cliente com contas a receber encontrado');
          setLoading(false);
          return;
        }
      } else {
        clientesEncontrados = clientesEncontrados.slice(0, 10);
      }
      
      setClientes(clientesEncontrados);
      
      if (clientesEncontrados.length === 1) {
        buscarContasReceber(clientesEncontrados[0]);
      }
      
    } catch (error) {
      console.error('❌ Erro ao buscar clientes:', error);
      setErro('Erro ao buscar clientes: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  /**
   * ⭐ Buscar contas a receber - CORRIGIDO PARA PIX
   */
  const buscarContasReceber = async (cliente) => {
    setLoadingLink(true);
    setErro(null);

    try {
      console.log(`🔍 Buscando contas para PIX - Cliente: ${cliente.Ordem}`);
      
      // ⭐⭐⭐ CRÍTICO: Passar para_pix=true ⭐⭐⭐
      const response = await apiService.clientes.buscarContas(cliente.Ordem, 1, true);
      //                                                                          👆 true para PIX
      
      console.log('📥 Resposta da API:', response.data);
      
      let contasEncontradas = [];
      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          contasEncontradas = response.data.data;
        } else if (response.data.contas && Array.isArray(response.data.contas)) {
          contasEncontradas = response.data.contas;
        } else if (Array.isArray(response.data)) {
          contasEncontradas = response.data;
        }
      }
      
      if (contasEncontradas.length > 0) {
        setContasReceber(contasEncontradas);
        setClienteSelecionado(cliente);
        setExpandido({ [cliente.Ordem]: true });
        console.log(`✅ Encontradas ${contasEncontradas.length} parcelas em aberto`);
      } else {
        setErro('Nenhuma conta a receber encontrada para este cliente');
        setContasReceber([]);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar contas:', error);
      setErro('Erro ao buscar contas a receber: ' + (error.response?.data?.message || error.message));
      setContasReceber([]);
    } finally {
      setLoadingLink(false);
    }
  };

  /**
   * ⭐ Abrir modal para gerar link PIX de conta a receber
   */
  const abrirModalLink = (conta) => {
    setContaSelecionada(conta);
    setVendaSelecionada(null); // Limpar venda selecionada
    
    const valorConta = conta.Valor_Total;
    setValorLink(valorConta.toFixed(2));
    setValorMinimo(valorConta);
    setMensagem(`Pagamento referente à conta vencida em ${format(new Date(conta.Data_Vencimento), 'dd/MM/yyyy')}`);
    setMotivoAcrescimo('');
    
    // Telefone
    const telefoneCliente = clienteSelecionado.Fone_1 || clienteSelecionado.Fone_2 || '';
    const telefoneFormatado = formatarTelefone(telefoneCliente);
    setCelular(telefoneFormatado);
    setCelularOriginal(telefoneCliente);
    
    // Email
    setEmail('');
    
    // CPF/CNPJ
    const cpfCnpjCliente = clienteSelecionado.CPF || clienteSelecionado.CNPJ || '';
    const cpfCnpjFormatado = formatarCpfCnpj(cpfCnpjCliente);
    setCpfCnpj(cpfCnpjFormatado);
    setCpfCnpjOriginal(cpfCnpjCliente);
    
    setDiasExpiracao(1);
    setModalAberto(true);
  };

  /**
   * ⭐ ATUALIZADO: Gerar link de pagamento PIX
   */
  const gerarLinkPix = async () => {
    // Validações de valor
    if (!valorLink || parseFloat(valorLink) <= 0) {
      setErro('Valor inválido');
      return;
    }

    const valorSolicitado = parseFloat(valorLink);
    const temAcrescimo = valorSolicitado > valorMinimo;

    if (valorSolicitado < valorMinimo) {
      setErro('O valor não pode ser menor que o valor original');
      return;
    }

    // Se houver acréscimo, exigir motivo
    if (temAcrescimo && !motivoAcrescimo.trim()) {
      setErro('Obrigatório informar o motivo do acréscimo no valor');
      return;
    }

    // Validar que há pelo menos um contato (celular OU email)
    const temCelular = celular && celular.trim() !== '';
    const temEmail = email && email.trim() !== '';
    
    // Validar celular se preenchido
    if (temCelular) {
      const celularLimpo = limparTelefone(celular);
      if (celularLimpo.length < 12 || celularLimpo.length > 13) {
        setErro('Número de celular inválido. Use o formato: (85) 98765-4321');
        return;
      }
    }

    // Validar email se preenchido
    if (temEmail && !validarEmail(email)) {
      setErro('Email inválido');
      return;
    }

    // Validar CPF/CNPJ (OBRIGATÓRIO)
    if (!cpfCnpj || cpfCnpj.trim() === '') {
      setErro('CPF ou CNPJ é obrigatório');
      return;
    }

    if (!validarCpfCnpj(cpfCnpj)) {
      setErro('CPF ou CNPJ inválido. Verifique o número digitado');
      return;
    }

    // Validar dias de expiração
    if (!diasExpiracao || diasExpiracao < 1 || diasExpiracao > 30) {
      setErro('Dias de expiração deve estar entre 1 e 30');
      return;
    }

    // VERIFICAR SE WHATSAPP ESTÁ CONECTADO (apenas se for enviar por WhatsApp)
    //if (temCelular && !whatsappConectado) {
    //  setErro('WhatsApp não está conectado. Remova o celular ou conecte o WhatsApp antes de gerar o link.');
    //  return;
    //}

    setLoadingLink(true);
    setErro(null);

    try {
      const celularLimpo = temCelular ? limparTelefone(celular) : undefined;
      
      // Preparar descrição completa
      let descricaoCompleta = mensagem;
      if (temAcrescimo && motivoAcrescimo.trim()) {
        descricaoCompleta += `\n\n⚠️ ACRÉSCIMO: ${motivoAcrescimo}`;
      }
      
      // Dados base
      const dados = {
        valor: valorSolicitado,
        descricao: descricaoCompleta,
        cpf_cnpj: cpfCnpj.replace(/\D/g, ''),
        dias_expiracao: diasExpiracao
      };

      // TIPO CONTA A RECEBER
      if (contaSelecionada) {
        dados.ordem_cliente = clienteSelecionado.Ordem;
        dados.codigo_cliente = clienteSelecionado.Codigo;
        dados.nome_cliente = clienteSelecionado.Nome;
        dados.data_vencimento = contaSelecionada.Data_Vencimento;
        dados.contas_receber = [contaSelecionada.Ordem];
        dados.tipo_pix = 'parcela'; 
      }
      // TIPO VENDA À VISTA
      else if (vendaSelecionada) {
        dados.ordem_cliente = vendaSelecionada.Ordem_Cli_For;
        dados.codigo_cliente = vendaSelecionada.Codigo_Cli_For;
        dados.nome_cliente = vendaSelecionada.Nome_Cli_For;
        dados.ordem_venda = vendaSelecionada.Ordem;
        dados.sequencia_venda = vendaSelecionada.Sequencia;
        dados.tipo_pix = 'venda_avista';
      }

      console.log('💰 Dados FINAIS enviados:', JSON.stringify(dados, null, 2));

      const response = await apiService.pix.criar(dados);

      console.log('📥 Resposta PIX:', response);

      if (response.success || response.data?.success) {
        const linkData = response.data?.data || response.data;
        
        let statusEnvio = {
          whatsapp: false,
          email: false
        };

        // ENVIAR AUTOMATICAMENTE POR WHATSAPP (se houver celular)
        // PARA (duas mensagens separadas via rota dedicada):
        if (temCelular) {
          try {
            console.log('📱 Enviando PIX (2 mensagens) para:', celularLimpo);

            const parcelas = contaSelecionada
              ? [{ nota: contaSelecionada.Nota, valor: contaSelecionada.Valor_Total, vencimento: contaSelecionada.Data_Vencimento }]
              : [];

            const responseWhatsApp = await apiService.post('/api/whatsapp/enviar-pix', {
              telefone: celularLimpo,
              nomeCliente: dados.nome_cliente,
              codigoCliente: dados.codigo_cliente,
              valor: valorSolicitado,
              pixCopiaECola: linkData.pix?.qr_code || '',
              dataExpiracao: linkData.pix?.expires_at || null,
              parcelas
            });

            if (responseWhatsApp?.data?.success) {
              statusEnvio.whatsapp = true;
              console.log('✅ WhatsApp enviado (2 mensagens)');
            } else {
              console.error('❌ Erro ao enviar WhatsApp:', responseWhatsApp?.data);
            }
          } catch (errorWhatsApp) {
            console.error('❌ Erro ao enviar por WhatsApp:', errorWhatsApp);
          }
        }

        // ENVIAR POR EMAIL (se houver email)
        if (temEmail) {
          try {
            console.log('📧 Enviando link PIX por email para:', email);
            
            const dadosEmail = {
              para: email,
              assunto: 'Link de Pagamento PIX',
              ordem_cliente: dados.ordem_cliente,
              nome_cliente: dados.nome_cliente,
              valor: valorSolicitado,
              link: linkData.checkout_url,
              qr_code: linkData.pix?.qr_code,
              descricao: descricaoCompleta,
              expires_at: linkData.pix?.expires_at
            };
            
            if (contaSelecionada) {
              dadosEmail.data_vencimento = contaSelecionada.Data_Vencimento;
            } else if (vendaSelecionada) {
              dadosEmail.sequencia_venda = vendaSelecionada.Sequencia;
            }

            const responseEmail = await apiService.email?.enviar(dadosEmail);

            console.log('📥 Resposta Email completa:', responseEmail);

            if (responseEmail?.success || responseEmail?.data?.success || responseEmail?.status === 200) {
              statusEnvio.email = true;
              console.log('✅ Email enviado com sucesso');
            } else {
              console.error('❌ Erro ao enviar email - resposta inesperada:', responseEmail);
            }
          } catch (errorEmail) {
            console.error('❌ Erro ao enviar por email:', errorEmail);
            if (errorEmail?.response?.status === 200 || errorEmail?.status === 200) {
              statusEnvio.email = true;
              console.log('✅ Email enviado com sucesso (detectado no catch)');
            }
          }
        }

        // ⭐ Preparar dados para janela - CORRIGIDO PARA VENDAS
        const dadosParaJanela = {
          ...linkData,
          tipo: contaSelecionada ? 'conta' : 'venda',

           // ✅ GARANTIR checkout_url
          checkout_url: linkData.checkout_url || linkData.pix?.qr_code_url || '#',
  

          cliente: {
            nome: dados.nome_cliente,
            codigo: dados.codigo_cliente,
            ordem: dados.ordem_cliente,
            cpf_cnpj: cpfCnpj
          },
          valor_cobrado: valorSolicitado,
          celular: celular ? formatarTelefone(celular) : null,
          email: email || null,
          status_envio: statusEnvio,
          timestamp: Date.now()
        };

        if (contaSelecionada) {
          // TIPO: Conta a Receber
          dadosParaJanela.conta = {
            nota: contaSelecionada.Nota,
            data_vencimento: contaSelecionada.Data_Vencimento,
            valor_original: contaSelecionada.Valor_Total,
            dias_atraso: contaSelecionada.Dias_Atraso
          };
        } else if (vendaSelecionada) {
          // TIPO: Venda à Vista
          dadosParaJanela.venda = {
            sequencia: vendaSelecionada.Sequencia,
            data: vendaSelecionada.Data,
            valor_original: vendaSelecionada.Valor_Liquido,
            ordem: vendaSelecionada.Ordem
          };
          
          // ⚠️ IMPORTANTE: Para venda à vista, criar objeto 'conta' com dados da venda
          // para que ResultadoCobrancaPix.jsx possa renderizar corretamente
          dadosParaJanela.conta = {
            nota: `Venda #${vendaSelecionada.Sequencia}`,
            data_vencimento: vendaSelecionada.Data,
            valor_original: vendaSelecionada.Valor_Liquido,
            dias_atraso: 0
          };
        }

        if (temAcrescimo) {
          dadosParaJanela.acrescimo = {
            valor_original: valorMinimo,
            valor_acrescimo: valorSolicitado - valorMinimo,
            motivo: motivoAcrescimo
          };
        }

        console.log('📦 Dados completos para janela:', JSON.stringify(dadosParaJanela, null, 2));

        
        // ⭐ SALVAR (igual LinkPixAleatorio)
        salvarCobrancaPix(dadosParaJanela);
        console.log('💾 Dados salvos no localStorage');
        
        
        
        // ⭐ Abrir popup usando utilitário
        const resultadoPopup = await pixPopupUtils.openResultadoCobranca(dadosParaJanela);
        
        // Mensagem de sucesso baseada no resultado do popup
        let mensagemSucesso = 'Link PIX gerado com sucesso! ';
        
        if (resultadoPopup.success) {
          mensagemSucesso += 'Uma nova janela foi aberta. ✅';
        } else if (resultadoPopup.fallbackUsed) {
          mensagemSucesso += 'Popup bloqueado - verifique os resultados na mesma janela. ⚠️';
        } else {
          mensagemSucesso += 'Verifique os resultados. ✅';
        }
        
        if (statusEnvio.whatsapp && statusEnvio.email) {
          mensagemSucesso += ' Enviado por WhatsApp e Email! 📱📧';
        } else if (statusEnvio.whatsapp) {
          mensagemSucesso += ' Enviado por WhatsApp! 📱';
        } else if (statusEnvio.email) {
          mensagemSucesso += ' Enviado por Email! 📧';
        }
        
        setSucesso(mensagemSucesso);
        
        // Fechar modal após 2 segundos
        setTimeout(() => {
          fecharModal();
          setSucesso(null);
        }, 2000);
        
      } else {
        setErro(response.error || response.data?.error || 'Erro ao gerar link PIX');
      }
    } catch (error) {
      console.error('❌ Erro ao gerar link PIX:', error);
      
      let mensagemErro = 'Erro ao gerar link PIX';
      
      if (error.response?.data?.message) {
        mensagemErro = error.response.data.message;
      } else if (error.response?.data?.error) {
        mensagemErro = error.response.data.error;
      } else if (error.message) {
        mensagemErro += ': ' + error.message;
      }
      
      console.error('📋 Detalhes do erro:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      setErro(mensagemErro);
    } finally {
      setLoadingLink(false);
    }
  };

  /**
   * Fechar modal
   */
  const fecharModal = () => {
    setModalAberto(false);
    setContaSelecionada(null);
    setVendaSelecionada(null);
    setValorLink('');
    setValorMinimo(0);
    setMensagem('');
    setMotivoAcrescimo('');
    setCelular('');
    setCelularOriginal('');
    setEmail('');
    setCpfCnpj('');
    setCpfCnpjOriginal('');
    setDiasExpiracao(1);
    setErro(null);
  };

  /**
   * Formatar moeda
   */
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  /**
   * Renderizar badge de status de conta
   */
  const renderStatusBadge = (diasAtraso) => {
    if (diasAtraso <= 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Em dia
        </span>
      );
    } else if (diasAtraso <= 30) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          {diasAtraso} dias
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          {diasAtraso} dias
        </span>
      );
    }
  };

  /**
   * Permitir busca com Enter
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (tipoCobranca === 'contas') {
        buscarClientes();
      } else {
        buscarVenda();
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <QrCode className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Cobrança PIX</h1>
              <p className="text-indigo-100 mt-1">
                Gere links de pagamento via Pagar.me e envie por WhatsApp ou Email
              </p>
            </div>
          </div>
          
          {/* INDICADOR DE CONEXÃO WHATSAPP */}
          <div className="flex items-center space-x-2">
            {verificandoWhatsapp ? (
              <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Verificando...</span>
              </div>
            ) : whatsappConectado ? (
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 rounded-lg border border-green-400">
                <Wifi className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">WhatsApp Conectado</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 px-4 py-2 bg-yellow-500/20 rounded-lg border border-yellow-400">
                <WifiOff className="w-5 h-5 text-yellow-300" />
                <span className="text-sm font-medium">Apenas Email</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alerta se WhatsApp não estiver conectado */}
      {!whatsappConectado && !verificandoWhatsapp && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">WhatsApp desconectado</p>
            <p className="text-sm text-yellow-700 mt-1">
              Você ainda pode gerar links PIX e enviar por email. 
              Para enviar por WhatsApp, acesse a página de <a href="/configuracoes" className="underline font-medium">Configurações</a> para conectar.
            </p>
          </div>
        </div>
      )}

      {/* Mensagens de feedback */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Erro</p>
            <p className="text-sm text-red-700 mt-1">{erro}</p>
          </div>
          <button 
            onClick={() => setErro(null)}
            className="text-red-400 hover:text-red-600"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {sucesso && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">Sucesso</p>
            <p className="text-sm text-green-700 mt-1">{sucesso}</p>
          </div>
          <button 
            onClick={() => setSucesso(null)}
            className="text-green-400 hover:text-green-600"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ⭐ SELETOR DE TIPO DE COBRANÇA */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tipo de Cobrança</h2>
          
          <div className="flex space-x-4">
            <button
              onClick={() => {
                setTipoCobranca('contas');
                setSequenciaVenda('');
                setVendaSelecionada(null);
                setErro(null);
              }}
              className={`flex-1 flex items-center justify-center space-x-3 px-6 py-4 rounded-lg border-2 transition-all ${
                tipoCobranca === 'contas'
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Receipt className="w-6 h-6" />
              <div className="text-left">
                <p className="font-semibold">Contas a Receber</p>
                <p className="text-xs">Cobrar parcelas em atraso</p>
              </div>
            </button>

            <button
              onClick={() => {
                setTipoCobranca('venda');
                setBusca('');
                setClientes([]);
                setClienteSelecionado(null);
                setErro(null);
              }}
              className={`flex-1 flex items-center justify-center space-x-3 px-6 py-4 rounded-lg border-2 transition-all ${
                tipoCobranca === 'venda'
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ShoppingCart className="w-6 h-6" />
              <div className="text-left">
                <p className="font-semibold">Venda à Vista</p>
                <p className="text-xs">Receber venda no caixa</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* ⭐ BUSCA DE VENDA (quando selecionado) */}
      {tipoCobranca === 'venda' && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ShoppingCart className="w-5 h-5 mr-2 text-gray-600" />
              Buscar Venda por Sequência
            </h2>
          </div>

          <div className="p-6">
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número da Sequência da Venda
                </label>
                <input
                  type="number"
                  value={sequenciaVenda}
                  onChange={(e) => setSequenciaVenda(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onWheel={(e) => e.target.blur()}
                  placeholder="Digite o número da sequência..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={buscarVenda}
                  disabled={loadingVenda}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingVenda ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Buscando...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      <span>Buscar Venda</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Exibir venda encontrada */}
            {vendaSelecionada && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800 mb-2">
                      ✅ Venda Encontrada
                    </p>
                    <div className="space-y-1 text-sm text-green-700">
                      <p><span className="font-medium">Sequência:</span> {vendaSelecionada.Sequencia}</p>
                      <p><span className="font-medium">Cliente:</span> {vendaSelecionada.Nome_Cli_For}</p>
                      <p><span className="font-medium">Data:</span> {format(new Date(vendaSelecionada.Data), 'dd/MM/yyyy')}</p>
                      <p><span className="font-medium">Valor:</span> {formatarMoeda(vendaSelecionada.Valor_Liquido)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => abrirModalVenda(vendaSelecionada)}
                    className="ml-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>Gerar Link PIX</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* BUSCA DE CLIENTES (quando selecionado contas a receber) */}
      {tipoCobranca === 'contas' && (
        <>
          <div className="bg-white rounded-lg shadow-md border border-gray-200">
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Search className="w-5 h-5 mr-2 text-gray-600" />
                Buscar Cliente
              </h2>
            </div>

            <div className="p-6">
              {/* Toggle para filtrar apenas clientes com contas */}
              <div className="mb-4 flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center space-x-3">
                  <Filter className="w-5 h-5 text-indigo-600" />
                  <label className="text-sm font-medium text-gray-700">
                    Mostrar apenas clientes com contas a receber
                  </label>
                </div>
                <button
                  onClick={() => setApenasComContas(!apenasComContas)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    apenasComContas ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={apenasComContas}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      apenasComContas ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex space-x-4">
                {/* Tipo de busca */}
                <div className="w-40">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar por
                  </label>
                  <select
                    value={tipoBusca}
                    onChange={(e) => setTipoBusca(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="nome">Nome</option>
                    <option value="codigo">Código</option>
                  </select>
                </div>

                {/* Campo de busca */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tipoBusca === 'nome' ? 'Nome do cliente' : 'Código do cliente'}
                  </label>
                  <input
                    type={tipoBusca === 'codigo' ? 'number' : 'text'}
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={tipoBusca === 'nome' ? 'Digite o nome...' : 'Digite o código...'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Botão buscar */}
                <div className="flex items-end">
                  <button
                    onClick={buscarClientes}
                    disabled={loading}
                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Buscando...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        <span>Buscar</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de clientes encontrados */}
          {clientes.length > 0 && (
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-gray-600" />
                  Clientes Encontrados ({clientes.length})
                  {apenasComContas && (
                    <span className="ml-2 px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                      Com contas a receber
                    </span>
                  )}
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                {clientes.map((cliente) => (
                  <div key={cliente.Ordem} className="p-6">
                    {/* Cabeçalho do cliente */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                              <Users className="w-6 h-6 text-indigo-600" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-gray-900">
                              {cliente.Nome}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Código: {cliente.Codigo} | CPF/CNPJ: {cliente.CPF || cliente.CNPJ || 'N/A'}
                            </p>
                            {(cliente.Fone_1 || cliente.Fone_2) && (
                              <p className="text-sm text-gray-500 flex items-center mt-1">
                                <Phone className="w-3 h-3 mr-1" />
                                {formatarTelefone(cliente.Fone_1 || cliente.Fone_2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Botão expandir */}
                      <button
                        onClick={() => {
                          if (expandido[cliente.Ordem]) {
                            setExpandido({ ...expandido, [cliente.Ordem]: false });
                          } else {
                            buscarContasReceber(cliente);
                          }
                        }}
                        className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {expandido[cliente.Ordem] ? (
                          <ChevronUp className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                    </div>

                    {/* Contas a receber (expandido) */}
                    {expandido[cliente.Ordem] && (
                      <div className="mt-6 border-t border-gray-200 pt-6">
                        {loadingLink && clienteSelecionado?.Ordem === cliente.Ordem ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                            <span className="ml-3 text-gray-600">Carregando contas...</span>
                          </div>
                        ) : contasReceber.length > 0 ? (
                          <div className="space-y-3">
                            <h4 className="text-sm font-medium text-gray-900 mb-4">
                              Contas a Receber
                            </h4>
                            {contasReceber.map((conta) => (
                              <div
                                key={conta.Ordem}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-700">
                                      Vencimento: {format(new Date(conta.Data_Vencimento), 'dd/MM/yyyy', { locale: ptBR })}
                                    </span>
                                    {renderStatusBadge(conta.Dias_Atraso)}
                                  </div>
                                  <div className="mt-2 flex items-baseline space-x-2">
                                    <span className="text-lg font-semibold text-gray-900">
                                      {formatarMoeda(conta.Valor_Total)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      Nota: {conta.Nota}
                                    </span>
                                  </div>
                                </div>

                                {/* Botão gerar link */}
                                <button
                                  onClick={() => abrirModalLink(conta)}
                                  className="ml-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
                                >
                                  <QrCode className="w-4 h-4" />
                                  <span>Gerar Link PIX</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            Nenhuma conta a receber encontrada
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de formulário */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Cabeçalho do modal */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <QrCode className="w-6 h-6 mr-2" />
                  Gerar Link de Pagamento PIX
                  {vendaSelecionada && (
                    <span className="ml-3 px-2 py-1 bg-white/20 rounded text-sm">
                      Venda à Vista
                    </span>
                  )}
                </h3>
                <button
                  onClick={fecharModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Corpo do modal */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Informações do cliente */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Cliente</h4>
                  <p className="text-base font-semibold text-gray-800">
                    {vendaSelecionada ? vendaSelecionada.Nome_Cli_For : clienteSelecionado?.Nome}
                  </p>
                  <p className="text-sm text-gray-600">
                    Código: {vendaSelecionada ? vendaSelecionada.Codigo_Cli_For : clienteSelecionado?.Codigo}
                  </p>
                </div>

                {/* Informações da conta OU venda */}
                {contaSelecionada ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Conta Original</h4>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600">
                          Vencimento: {format(new Date(contaSelecionada.Data_Vencimento), 'dd/MM/yyyy')}
                        </p>
                        <p className="text-sm text-gray-600">
                          Nota: {contaSelecionada.Nota}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatarMoeda(contaSelecionada.Valor_Total)}
                        </p>
                        {renderStatusBadge(contaSelecionada.Dias_Atraso)}
                      </div>
                    </div>
                  </div>
                ) : vendaSelecionada ? (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Venda à Vista
                    </h4>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-blue-700">
                          Sequência: #{vendaSelecionada.Sequencia}
                        </p>
                        <p className="text-sm text-blue-700">
                          Data: {format(new Date(vendaSelecionada.Data), 'dd/MM/yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-blue-900">
                          {formatarMoeda(vendaSelecionada.Valor_Liquido)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Campo de CPF/CNPJ (OBRIGATÓRIO) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CPF ou CNPJ * 
                    <span className="text-red-600 ml-1">(Obrigatório)</span>
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={cpfCnpj}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/\D/g, '');
                        if (valor.length <= 14) {
                          setCpfCnpj(formatarCpfCnpj(valor));
                        }
                      }}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base ${
                        cpfCnpj && !validarCpfCnpj(cpfCnpj) 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {cpfCnpj && !validarCpfCnpj(cpfCnpj) && (
                    <p className="mt-2 text-xs text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      CPF ou CNPJ inválido
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    * Necessário para identificação na Pagar.me
                  </p>
                </div>

                {/* Campo de Celular (OPCIONAL) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Celular para envio do link
                    <span className="text-gray-500 ml-1">(Opcional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={celular}
                      onChange={(e) => setCelular(e.target.value)}
                      placeholder="(85) 98765-4321"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500 flex items-center">
                    {whatsappConectado ? (
                      <>
                        <Send className="w-3 h-3 mr-1" />
                        O link será enviado automaticamente por WhatsApp se preenchido
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-3 h-3 mr-1" />
                        WhatsApp desconectado - use email para envio
                      </>
                    )}
                  </p>
                </div>

                {/* Campo de Email (OPCIONAL) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email para envio do link
                    m•µ bn<span className="text-gray-500 ml-1">(Opcional)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="cliente@exemplo.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500 flex items-center">
                    <Mail className="w-3 h-3 mr-1" />
                    Se preenchido, o link também será enviado por email
                  </p>
                </div>

                {/* Campo de valor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valor a Cobrar *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min={valorMinimo}
                      value={valorLink}
                      onChange={(e) => setValorLink(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg font-semibold ${
                        parseFloat(valorLink) > valorMinimo ? 'border-yellow-300 bg-yellow-50' : 'border-gray-300'
                      }`}
                      placeholder="0,00"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    * Valor mínimo: {formatarMoeda(valorMinimo)} - pode ser aumentado mas não diminuído
                  </p>
                  {parseFloat(valorLink) > valorMinimo && (
                    <p className="mt-1 text-xs text-yellow-700 font-medium flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Acréscimo de {formatarMoeda(parseFloat(valorLink) - valorMinimo)} - motivo obrigatório abaixo
                    </p>
                  )}
                </div>

                {/* ⭐ Campo de Motivo do Acréscimo (aparece só se houver acréscimo) */}
                {parseFloat(valorLink) > valorMinimo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo do Acréscimo *
                      <span className="text-red-600 ml-1">(Obrigatório)</span>
                    </label>
                    <textarea
                      value={motivoAcrescimo}
                      onChange={(e) => setMotivoAcrescimo(e.target.value)}
                      rows={2}
                      className="w-full px-4 py-3 border border-yellow-300 bg-yellow-50 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                      placeholder="Ex: Juros de atraso, taxa de serviço, etc..."
                    />
                    {!motivoAcrescimo.trim() && (
                      <p className="mt-2 text-xs text-red-600 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Obrigatório informar o motivo quando o valor for maior que o original
                      </p>
                    )}
                  </div>
                )}

                {/* Campo de Dias para Expiração */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dias para Expiração do PIX *
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={diasExpiracao}
                      onChange={(e) => {
                        const valor = parseInt(e.target.value);
                        if (valor >= 1 && valor <= 30) {
                          setDiasExpiracao(valor);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
                      placeholder="1"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    * Número de dias até o link PIX expirar (mínimo: 1, máximo: 30)
                  </p>
                </div>

                {/* Campo de mensagem */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem/Descrição
                  </label>
                  <textarea
                    value={mensagem}
                    onChange={(e) => setMensagem(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    placeholder="Digite uma mensagem para o cliente..."
                  />
                </div>

                {/* Erro dentro do modal */}
                {erro && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">Erro</p>
                      <p className="text-sm text-red-700 mt-1">{erro}</p>
                    </div>
                    <button onClick={() => setErro(null)} className="text-red-400 hover:text-red-600">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Botões */}
                <div className="flex space-x-3">
                  <button
                    onClick={fecharModal}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={gerarLinkPix}
                    disabled={
                      loadingLink || 
                      !valorLink || 
                      parseFloat(valorLink) <= 0 || 
                      parseFloat(valorLink) < valorMinimo ||
                      (parseFloat(valorLink) > valorMinimo && !motivoAcrescimo.trim()) ||
                      !cpfCnpj ||
                      !validarCpfCnpj(cpfCnpj) ||
                      !diasExpiracao ||
                      diasExpiracao < 1 ||
                      diasExpiracao > 30
                    }
                    className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingLink ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Gerando e enviando...</span>
                      </>
                    ) : (
                      <>
                        <QrCode className="w-5 h-5" />
                        <span>Gerar e Enviar</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}